'use client'

import { CartesianGrid, Line, LineChart, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import type { ChartPoint } from '../_lib/calculateCoastFire'

interface Props {
  chartData: ChartPoint[]
  coastAge: number | null
}

function fmtAxis(v: number): string {
  if (v >= 1_000_000) return '$' + (v / 1_000_000).toFixed(1) + 'M'
  if (v >= 1_000) return '$' + Math.round(v / 1_000) + 'K'
  return '$' + v
}

function fmtTooltip(v: number): string {
  return '$' + Math.round(v).toLocaleString('en-US')
}

const crossoverPoint = (chartData: ChartPoint[], coastAge: number | null) => {
  if (coastAge === null) return null
  return chartData.find((p) => p.age === coastAge) ?? null
}

// SVG presentation attributes don't support CSS custom properties — map tokens to hex values here.
const C = {
  axis: '#8b8c8f', // --color-neutral-dark
  tooltipBg: '#111111', // --color-gray-darker
  tooltipLabel: '#faf7f2', // --color-bone-light
  tooltipItem: 'rgba(250, 247, 242, 0.75)', // --color-bone-light at 75% opacity
  portfolioLine: '#378add', // --color-blue-medium
  thresholdLine: '#2a8a6e', // --color-green
  crossoverDot: '#d85a30', // --color-orange
} as const

export function CoastFireChart({ chartData, coastAge }: Props) {
  const dot = crossoverPoint(chartData, coastAge)

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 8 }}>
        <CartesianGrid stroke="rgba(0,0,0,0.05)" />
        <XAxis
          dataKey="age"
          label={{ value: 'Age', position: 'insideBottom', offset: -4, style: { fill: C.axis, fontSize: 12 } }}
          tick={{ fill: C.axis, fontSize: 11 }}
          tickCount={12}
        />
        <YAxis
          tickFormatter={fmtAxis}
          tick={{ fill: C.axis, fontSize: 11 }}
          label={{
            value: 'Portfolio value',
            angle: -90,
            position: 'insideLeft',
            offset: 12,
            style: { fill: C.axis, fontSize: 12 },
          }}
        />
        <Tooltip
          contentStyle={{ backgroundColor: C.tooltipBg, border: 'none', borderRadius: 8 }}
          labelStyle={{ color: C.tooltipLabel, fontWeight: 600 }}
          itemStyle={{ color: C.tooltipItem }}
          labelFormatter={(label) => `Age ${label}`}
          formatter={(value, name) =>
            typeof value === 'number'
              ? [fmtTooltip(value), String(name ?? '')]
              : [String(value ?? ''), String(name ?? '')]
          }
        />
        <Line
          type="monotone"
          dataKey="portfolio"
          name="Your projected portfolio"
          stroke={C.portfolioLine}
          strokeWidth={2.5}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          type="monotone"
          dataKey="threshold"
          name="Coast FIRE threshold"
          stroke={C.thresholdLine}
          strokeWidth={2}
          strokeDasharray="7 4"
          dot={false}
        />
        {dot !== null && (
          <ReferenceDot
            x={dot.age}
            y={dot.portfolio}
            r={8}
            fill={C.crossoverDot}
            stroke={C.crossoverDot}
            label={{ value: `Age ${dot.age}`, position: 'top', fill: C.crossoverDot, fontSize: 11 }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  )
}
